import { ChevronLeft, ChevronRight } from "@/components/ui/icons";
import { router, useLocalSearchParams } from "expo-router";
import { useEffect, useMemo, useRef } from "react";
import { Image, Pressable, ScrollView, Text, useWindowDimensions, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { ShowHallStrip } from "@/components/floor-plan/ShowHallStrip";
import { PublicShowSkeleton } from "@/components/shows/PublicShowSkeleton";
import { Badge, ConditionChip } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Screen } from "@/components/ui/Screen";
import { useMarkInteractive } from "@/hooks/useMarkInteractive";
import { usePublicShow } from "@/hooks/usePublicShow";
import { usePublicShowCard } from "@/hooks/usePublicShowInventory";
import { loadShowAnalytics } from "@/lib/browse/showAnalyticsClient";
import { formatCardIdentity, formatGradeLabel } from "@/lib/catalog/cardIdentity";
import { parseFloorPlan } from "@/lib/shows/floorPlan";
import { availabilityLabel, sortCardVendors, treatmentLabel } from "@/lib/shows/showCardFinding";
import { describeShowLocation, shouldCollapseShowMap } from "@/lib/shows/showWayfinding";
import { useThemeColors } from "@/theme/theme";

const one = (value: string | string[] | undefined) => Array.isArray(value) ? value[0] : value;
const atTable = (numbers: readonly string[]) =>
  numbers.length ? `at table ${numbers.join(", ")}` : "with no table assigned";

export default function PublicShowCardScreen() {
  const params = useLocalSearchParams<{ cardId: string; q?: string; showId: string; }>();
  const showId = one(params.showId);
  const cardId = one(params.cardId);
  const query = one(params.q) ?? "";
  const insets = useSafeAreaInsets();
  const { fontScale } = useWindowDimensions();
  const c = useThemeColors();
  const showQuery = usePublicShow(showId);
  const cardQuery = usePublicShowCard(showId, cardId);
  // Deep link straight to one card at a show — both queries have to settle before the customer
  // knows which table to walk to, which is the whole point of the page.
  useMarkInteractive(!showQuery.isLoading && !cardQuery.isLoading);
  const tracked = useRef(false);

  const plan = useMemo(() => {
    const source = showQuery.show?.plan?.document;
    if (!source) return null;
    try {
      return parseFloorPlan(typeof source === "string" ? source : JSON.stringify(source));
    } catch {
      return null;
    }
  }, [showQuery.show?.plan?.document]);
  const vendors = useMemo(
    () => sortCardVendors(cardQuery.data?.vendors ?? []),
    [cardQuery.data?.vendors],
  );
  const tableIds = useMemo(() => {
    const byNumber = new Map(plan?.tables.map((table) => [table.number, table.id]) ?? []);
    return vendors.map((vendor) => ({
      participationId: vendor.participation_id,
      tableIds: vendor.table_numbers.flatMap((number) => {
        const id = byNumber.get(number);
        return id ? [id] : [];
      }),
    }));
  }, [plan, vendors]);
  const mapCollapsed = !!plan && shouldCollapseShowMap(plan, tableIds, fontScale);

  useEffect(() => {
    const show = showQuery.show;
    const detail = cardQuery.data;
    if (!show || !detail || tracked.current) return;
    tracked.current = true;
    const showEndsAt = (show.sessions ?? []).map((session) => session.ends_at).sort().at(-1)
      ?? null;
    void loadShowAnalytics({ showId: show.public_id, showEndsAt }).then((analytics) => {
      analytics.cardOpened(show.public_id, detail.card.card_id, detail.card.vendor_count);
    });
  }, [cardQuery.data, showQuery.show]);

  const back = () => {
    const suffix = query ? `?${new URLSearchParams({ q: query })}` : "";
    router.dismissTo(`/shows/${encodeURIComponent(showId ?? "")}${suffix}`);
  };

  if (showQuery.isLoading || cardQuery.isLoading) {
    return <PublicShowSkeleton label="Loading card locations…" />;
  }

  if (!showQuery.show || !cardQuery.data || showQuery.error || cardQuery.error) {
    return (
      <Screen className="events-page items-center justify-center px-6" role="main">
        <Text className="text-lg font-title text-foreground">Card listing unavailable</Text>
        <Text className="mt-2 text-center font-body text-muted-foreground">
          This card may no longer be in the Show's public inventory.
        </Text>
        <View className="mt-5 flex-row gap-2">
          <Button title="Back to Show" variant="outline" onPress={back} />
          <Button title="Try again" variant="outline" onPress={() => void cardQuery.refetch()} />
        </View>
      </Screen>
    );
  }

  const { card } = cardQuery.data;
  const identity = formatCardIdentity(card.set_name, card.collector_number, treatmentLabel(card));

  return (
    <Screen testID="show-card-detail" className="events-page" role="main">
      {/* Every card on the events host shared one tab and link-preview title (TRO-905). */}
      <View
        className="w-full max-w-[760px] self-center flex-row items-center gap-2 border-b border-border px-2 pb-3"
        style={{ paddingTop: insets.top + 8 }}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Back to Show search"
          onPress={back}
          className="h-11 w-11 items-center justify-center rounded-full active:bg-surface-muted"
        >
          <ChevronLeft size={22} color={c.foreground} />
        </Pressable>
        <View className="min-w-0 flex-1">
          <Text className="font-title text-foreground" numberOfLines={1}>
            {showQuery.show.name}
          </Text>
          <Text className="text-xs font-body text-muted-foreground" numberOfLines={1}>
            Back to {query ? `“${query}”` : "Show search"}
          </Text>
        </View>
      </View>

      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: insets.bottom + 32 }}>
        <View className="w-full max-w-[760px] self-center gap-4">
          <View className="flex-row items-center gap-4">
            {card.image_url
              ? (
                <Image
                  source={{ uri: card.image_url }}
                  className="h-28 w-20 rounded-lg bg-surface-muted"
                  resizeMode="cover"
                  accessible={false}
                  accessibilityIgnoresInvertColors
                />
              )
              : null}
            <View className="min-w-0 flex-1 gap-1">
              <Text
                accessibilityRole="header"
                aria-level={1}
                className="text-2xl font-title text-foreground"
              >
                {card.name}
              </Text>
              {identity
                ? <Text className="font-body text-muted-foreground">{identity}</Text>
                : null}
              <Badge
                label={availabilityLabel(card.vendor_count, card.quantity)}
                className="mt-1 bg-accent-soft"
                textClassName="text-primary"
              />
              {showQuery.show.phase === "upcoming"
                ? (
                  <Text className="mt-1 text-xs font-body leading-4 text-warning">
                    Inventory may change before doors open. Vendors keep selling from this stock
                    until then.
                  </Text>
                )
                : null}
            </View>
          </View>

          {plan && !mapCollapsed && tableIds.some((group) => group.tableIds.length)
            ? <ShowHallStrip document={plan} groups={tableIds} />
            : (
              <View
                testID="show-location"
                className="rounded-xl border border-border bg-surface p-4"
              >
                <Text className="font-title text-foreground">
                  Use the Vendor list for directions
                </Text>
                <Text className="mt-1 font-body text-muted-foreground">
                  {mapCollapsed
                    ? "At this text size, table locations are clearer as words."
                    : "The floor view is unavailable; every assigned table is still listed below."}
                </Text>
              </View>
            )}

          <View className="gap-2">
            <Text
              accessibilityRole="header"
              aria-level={2}
              className="text-lg font-title text-foreground"
            >
              Vendors with this card
            </Text>
            {vendors.map((vendor, index) => {
              const ids = tableIds[index]?.tableIds ?? [];
              const location = plan ? describeShowLocation(plan, ids) : null;
              return (
                <Pressable
                  key={vendor.participation_id}
                  testID="show-vendor-result"
                  accessibilityRole="button"
                  accessibilityLabel={`${index + 1}, ${vendor.name}, ${
                    atTable(vendor.table_numbers)
                  }`}
                  onPress={() => {
                    const suffix = query ? `?${new URLSearchParams({ q: query })}` : "";
                    router.push(
                      `/shows/${encodeURIComponent(showQuery.show!.public_id)}/vendors/${
                        encodeURIComponent(vendor.participation_id)
                      }${suffix}`,
                    );
                  }}
                  className="min-h-20 flex-row items-center gap-3 rounded-xl border border-border bg-surface p-3 active:bg-surface-muted"
                >
                  <View className="h-7 w-7 items-center justify-center rounded-full border-2 border-primary bg-surface">
                    <Text className="text-xs font-label text-primary">{index + 1}</Text>
                  </View>
                  <View className="min-w-0 flex-1 gap-1">
                    <Text className="font-title text-foreground" numberOfLines={1}>
                      {vendor.name}
                    </Text>
                    <Text className="text-xs font-body text-muted-foreground">
                      {atTable(vendor.table_numbers)}
                      {location ? ` · ${location}` : ""}
                    </Text>
                    <View className="flex-row flex-wrap gap-1">
                      {vendor.copies.map((copy, copyIndex) => {
                        const grade = formatGradeLabel(
                          copy.grading_company,
                          copy.grade,
                          copy.is_graded,
                        );
                        return copy.condition
                          ? (
                            <ConditionChip
                              key={`${copy.condition}-${copyIndex}`}
                              condition={`${copy.condition} ×${copy.quantity}`}
                            />
                          )
                          : (
                            <Badge
                              key={`${grade}-${copyIndex}`}
                              label={`${grade ?? "Condition not listed"} ×${copy.quantity}`}
                              className="bg-surface-muted"
                              textClassName="text-muted-foreground"
                            />
                          );
                      })}
                    </View>
                  </View>
                  <ChevronRight size={18} color={c.subtle} />
                </Pressable>
              );
            })}
          </View>
        </View>
      </ScrollView>
    </Screen>
  );
}
